import { useSearchParams, Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import PublicLayout from "@/components/PublicLayout";
import Seo from "@/components/Seo";
import { api, formatError } from "@/lib/api";
import { Loader2, ShieldCheck, ArrowRight } from "lucide-react";

/*
  Two public payment landing pages in one file:
   - /pay/deposit?lead=<leadId>            — customer pays $49 booking deposit
   - /pay/unlock?lead=<leadId>&contractor=<email>  — contractor pays $30 to unlock a lead
*/

function LandingCard({ purpose, title, blurb, price, ctaLabel, context }) {
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        purpose,
        origin_url: window.location.origin,
        context,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatError(err));
      setBusy(false);
    }
  };
  return (
    <div className="bg-white border border-border p-8 max-w-xl mx-auto">
      <ShieldCheck className="text-safety" size={28} />
      <h1 className="mt-4 font-display text-3xl md:text-4xl uppercase leading-tight">{title}</h1>
      <p className="mt-3 text-muted-foreground">{blurb}</p>
      <div className="mt-6 border-t border-border pt-6 flex items-baseline justify-between">
        <div>
          <p className="rs-eyebrow">Amount</p>
          <p className="mt-1 font-display text-4xl">A${price}</p>
        </div>
        <button data-testid={`pay-${purpose}`} onClick={start} disabled={busy} className="rs-btn-primary disabled:opacity-70">
          {busy ? <><Loader2 className="animate-spin mr-2" size={16}/> Redirecting…</> : <>{ctaLabel} <ArrowRight size={16} className="ml-2" /></>}
        </button>
      </div>
      <p className="text-xs text-muted-foreground mt-4">Secure payment via Stripe. You'll be redirected to complete the transaction.</p>
    </div>
  );
}

export function PayDeposit() {
  const [params] = useSearchParams();
  const leadId = params.get("lead") || "";
  if (!leadId) {
    return (
      <PublicLayout>
        <section className="py-20 rs-container max-w-xl text-center">
          <h1 className="font-display text-3xl uppercase">Missing enquiry reference</h1>
          <p className="text-muted-foreground mt-2">Deposit links come from Site Cleanup. Please use the link in your confirmation email.</p>
          <Link to="/" className="rs-btn-outline mt-6">Back to home</Link>
        </section>
      </PublicLayout>
    );
  }
  return (
    <PublicLayout>
      <Seo title="Pay booking deposit | Site Cleanup" />
      <section className="py-20">
        <div className="rs-container">
          <LandingCard
            purpose="customer_deposit"
            title="Confirm your booking deposit"
            blurb="A small deposit locks in the contractor's clean-up date and is deducted from your final invoice. If the job doesn't proceed for reasons on our side, the deposit is refunded."
            price="49"
            ctaLabel="Pay deposit securely"
            context={{ lead_id: leadId }}
          />
        </div>
      </section>
    </PublicLayout>
  );
}

export function PayUnlock() {
  const [params] = useSearchParams();
  const leadId = params.get("lead") || "";
  const contractorEmail = params.get("contractor") || "";
  if (!leadId) {
    return (
      <PublicLayout>
        <section className="py-20 rs-container max-w-xl text-center">
          <h1 className="font-display text-3xl uppercase">Missing lead reference</h1>
          <p className="text-muted-foreground mt-2">Unlock links are shared by Site Cleanup. Please use the link from your matched-lead email.</p>
          <Link to="/pricing" className="rs-btn-outline mt-6">See contractor pricing</Link>
        </section>
      </PublicLayout>
    );
  }
  return (
    <PublicLayout>
      <Seo title="Unlock lead | Site Cleanup" />
      <section className="py-20">
        <div className="rs-container">
          <LandingCard
            purpose="lead_unlock"
            title="Unlock this matched lead"
            blurb="Pay a one-off unlock fee to reveal the customer's contact details, address and site photos. Only pay when the job looks like a good fit for your business."
            price="30"
            ctaLabel="Unlock lead"
            context={{ lead_id: leadId, contractor_email: contractorEmail }}
          />
          <p className="text-center text-sm text-muted-foreground mt-6">
            Prefer to receive unlimited unlocks? <Link to="/pricing" className="text-safety font-semibold underline underline-offset-4">See Pro & Premium plans</Link>.
          </p>
        </div>
      </section>
    </PublicLayout>
  );
}
